import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { HasShellContext, withIsInShell } from "@insite/client-framework/Components/IsInShell";
import { useGetLinks } from "@insite/client-framework/Store/Links/LinksSelectors";
import translate from "@insite/client-framework/Translate";
import { LinkFieldValue } from "@insite/client-framework/Types/FieldDefinition";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import SkipNav from "@insite/content-library/Components/SkipNav";
import Button from "@insite/mobius/Button";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import GridContainer from "@insite/mobius/GridContainer";
import GridItem from "@insite/mobius/GridItem";
import Icon, { IconPresentationProps } from "@insite/mobius/Icon";
import ChevronLeft from "@insite/mobius/Icons/ChevronLeft";
import ChevronRight from "@insite/mobius/Icons/ChevronRight";
import Img, { ImgProps } from "@insite/mobius/Img";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import Typography from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { productCarouselStyles, ProductCarouselStyles } from "@store/Overrides/Widgets/Common/ProductCarousel";
import { visuallyHiddenStyles } from "@store/theme/common/mixins";
import { COMMON } from "@store/theme/design";
import { mediaOptions } from "@store/utilities/breakpointMediaQueries";
import breakpointValuesMediaQueries from "@store/utilities/breakpointValuesMediaQueries";
import { useEmblaCarousel } from "embla-carousel/react";
import * as React from "react";
import { css, ThemeProps, withTheme } from "styled-components";

interface ElementModel {
    fields: {
        title: string;
        image: string;
        link: LinkFieldValue;
    };
}

const enum fields {
    elements = "elements",
    maxNumberOfTiles = "maxNumberOfTiles",
    showNavButtons = "showNavButtons",
    fullScreenWidth = "fullScreenWidth",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.elements]: ElementModel[];
        [fields.fullScreenWidth]: boolean;
        [fields.maxNumberOfTiles]: number;
        [fields.showNavButtons]: boolean;
    };
}

type Props = OwnProps &
    ThemeProps<BaseTheme> &
    HasShellContext;

export interface CarouselBannerStyles extends ProductCarouselStyles {
    img?: ImgProps;
    iconStyle?: IconPresentationProps;
    linkStyle?: LinkPresentationProps;
    slidesContainer?: InjectableCss;
    carouselSlideInnerForNavButtons?: InjectableCss;
    wrapperShell: InjectableCss;
}

const buttonsLeftRightValues = ["-12px", "-20px", null, "-24px", "-50px"];
const carouselSlideInnerCss = css`
    width: 100%;
    height: 100%;
    position: relative;
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
`;

const prevNextArrowGritItem = css`
    position: absolute;
    z-index: 11;
    height: 100%;
    max-width: 40px;
    flex-basis: 40px;
    pointer-events: visible;
`;

export const carouselBannerStyles: CarouselBannerStyles = {
    ...productCarouselStyles,

    mainContainer: {
        offsetCss: css`
            position: relative;
        `,
        gap: 0,
        css: css`
            margin: 0 auto;
        `,
    },
    titleText: {
        forwardAs: "h2",
        css: css`
            ${visuallyHiddenStyles()}
        `,
    },
    carouselSlidesContainer: {
        css: css`
            margin: 0 -10px;
            display: flex;
        `,
    },
    carouselContainer: {
        css: css`
            overflow: hidden;
        `,
    },
    carouselSlide: {
        css: css`
            padding: 0;
            position: relative;
            flex: 0 0 auto;
            ${props =>
                breakpointValuesMediaQueries(
                    props.theme,
                    [
                        { values: ["100% !important", "50% !important", null], properties: ["max-width"] },
                    ],
                    mediaOptions.minMax,
                )}

            &::before,
            &::after {
                content: none;
            }
        `,
    },
    carouselGridItem: {
        width: 10,
        css: css`
            flex-basis: 100%;
            max-width: 100%;
        `,
    },
    carouselSlideInner: {
        css: carouselSlideInnerCss,
    },
    carouselSlideInnerForNavButtons: {
        css: css`
            ${carouselSlideInnerCss}
            padding: 0px 10px;
        `,
    },
    prevArrowGridItem: {
        width: 1,
        align: "middle",
        css: css`
            ${prevNextArrowGritItem}
            ${props =>
                breakpointValuesMediaQueries(
                    props.theme,
                    [
                        { values: buttonsLeftRightValues, properties: ["left"] },
                    ],
                    mediaOptions.min,
                )}
        `,
    },
    nextArrowGridItem: {
        width: 1,
        align: "middle",
        css: css`            
            ${prevNextArrowGritItem}
            ${props =>
                breakpointValuesMediaQueries(
                    props.theme,
                    [
                        { values: buttonsLeftRightValues, properties: ["right"] },
                    ],
                    mediaOptions.min,
                )}
        `,
    },
    slidesContainer: {
        css: css`
            display: flex;
            flex-direction: row;
            width: 100%;
            ${props =>
                breakpointValuesMediaQueries(
                    props.theme,
                    [
                        { values: ["space-around", null, null, "space-between", null], properties: ["justify-content"] },
                    ],
                    mediaOptions.min,
                )}
        `,
    },
    img: {
        css: css`
            max-width: 100%;
            height: auto;
        `,
    },
    iconStyle: {
        color: "primary",
    },
    linkStyle: {
        css: css`
            &:hover {
                img {
                    opacity: 0.85;
                }
            }
        `,
    },
    wrapperShell: {
        css: css`
            position: relative;
            background: #e5e5e5;
            padding: 10px;
            margin-bottom: 10px;
        `,
    },
};

const styles = carouselBannerStyles;

const shuffleArray = (arr:any[]) => {
    const newArr:any[] = [...arr];
    for (let i = newArr.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1));
        [newArr[i], newArr[j]] = [newArr[j], newArr[i]];
    }
    return newArr;
}

const CarouselBanner: React.FC<Props> = ({
    theme,
    fields,
    shellContext,
}) => {
    const links = useGetLinks(fields.elements, o => o.fields.link);

    const afterCarousel = React.createRef<HTMLSpanElement>();
    const { elements } = fields

    const [emblaRef, embla] = useEmblaCarousel();
    const [windowResizeTime, setWindowResizeTime] = React.useState(0);
    const [canScrollPrev, setCanScrollPrev] = React.useState(false);
    const [canScrollNext, setCanScrollNext] = React.useState(false);
    const [shuffledElements, setShuffledElements] = React.useState<ElementModel[]>([]);
    
    const setCanScroll = () => {
        if (shellContext.isInShell) {
            return;
        }
        setCanScrollPrev(!!embla && embla.canScrollPrev());
        setCanScrollNext(!!embla && embla.canScrollNext());
    };

    const getSlidesToScroll = React.useCallback(() => {
        const maxNumberOfTiles = fields.maxNumberOfTiles || 6;
        if (typeof window === "undefined") {
            return maxNumberOfTiles;
        }

        const maxSlideSize = (1 + (fields.fullScreenWidth ? COMMON.width.site : COMMON.width.content)) / (maxNumberOfTiles+1);
        const _slidesToScroll =  fields.fullScreenWidth
            ? Math.floor(window.innerWidth / maxSlideSize)
            : Math.floor(Math.min(window.innerWidth, COMMON.width.content) / maxSlideSize);

        return _slidesToScroll;
    }, [fields.maxNumberOfTiles]);

    const initSlidesToScroll = getSlidesToScroll();
    const [slidesToScroll, setSlidesToScroll] = React.useState(initSlidesToScroll);
    const [canUseCarousel, setCanUseCarousel] = React.useState(initSlidesToScroll < elements.length);

    const getDraggable = () => {
        if (typeof window === "undefined") {
            return false;
        }

        return window.innerWidth < theme.breakpoints.values[1];
    };

    const [draggable, setDraggable] = React.useState(getDraggable());
    const [elementsShuffled, setElementsShuffled] = React.useState(false);

    React.useEffect(() => {
        const newSlidesToScroll = getSlidesToScroll();
        
        setCanUseCarousel(newSlidesToScroll < shuffledElements.length);

        setSlidesToScroll(newSlidesToScroll);

        const newDraggable = getDraggable();
        setDraggable(newDraggable);
    }, [fields.maxNumberOfTiles, windowResizeTime]);

    React.useEffect(() => {
        if (embla) {
            embla.on("init", setCanScroll);
            embla.on("select", setCanScroll);
        }

        const onWindowResize = () => {
            setWindowResizeTime(Date.now());
        };

        window.addEventListener("resize", onWindowResize);
        onWindowResize();

        return () => {
            window.removeEventListener("resize", onWindowResize);
        };
    }, []);

    React.useEffect(() => {
        if (!elements || elements.length === 0) {
            return;
        }

        if (embla && fields.showNavButtons && canUseCarousel) {
            embla.reInit({
                align: "start",
                slidesToScroll: 1,
                draggable,
                loop: true,
            });
            setCanScroll();
        }
    }, [embla, slidesToScroll, draggable, elements]);

    React.useEffect(() => {
        if (!elements || elements.length === 0 || elementsShuffled) {
            return;
        }

        setShuffledElements(shuffleArray(elements) as ElementModel[]);
        setElementsShuffled(true);
    }, [elements]);

    if (!shuffledElements || shuffledElements.length === 0 || !elementsShuffled) {
        return null;
    }

    const title = translate("Banner Navigation"); // Hidden; only there for accessibility

    const getSlide = (element:ElementModel, index:number) => {
        const link = links[index];
        const content = element.fields.image ? (
            <Img className="img" src={element.fields.image} altText={element.fields.image} loading="lazy" {...styles.img} />
        ) : null;

        const style = fields.showNavButtons && canUseCarousel 
            ? { maxWidth: `calc(${(100 / slidesToScroll)}% + 0.1px)` } // embla carousel calculations are a little off and this fixes the ability to use the carousel in n+1 elements.
            : { maxWidth: `calc(${(100 / slidesToScroll)}% - 10px)`, ...(fields.showNavButtons && !canUseCarousel?{margin: '0 5px'}:{}) }
        
        return (
            <StyledWrapper
                {...styles.carouselSlide}
                key={element.fields.title}
                style={style}
            >
                <StyledWrapper
                    {...(fields.showNavButtons && canUseCarousel ? styles.carouselSlideInnerForNavButtons : styles.carouselSlideInner)}
                    data-test-selector="carousel_slide"
                >
                    {(link && link.url) ? (
                        <Link href={link.url} {...styles.linkStyle} title={element.fields.title}>
                            {content}
                        </Link>
                    ) : content}
                </StyledWrapper>
            </StyledWrapper>
        );
    };

    const prevButtonStyle = fields.fullScreenWidth ? { left: "10px" } : {};
    const nextButtonStyle = fields.fullScreenWidth ? { right: "10px" } : {};

    return (
        <>
            {shellContext.isEditing && (
                <StyledWrapper {...styles.wrapperShell}>
                    <Typography>{`CMS notice: Please note that you should set the value for "Number Of Tiles" based on the width of the images of your tiles and how much of theses would fit in ${COMMON.width.site}px which is the content width of the site. If you also check fullscreen (first don't forget to NOT put this widget inside a Row in this case), the rule is the same, but know that at resolutions higher than ${COMMON.width.site}px, more tiles will be shown to keep your tiles at their maximum width size.`}</Typography>
                </StyledWrapper>
            )}
            <SkipNav
                text={translate("Skip Carousel")}
                extendedStyles={styles.skipCarouselButton}
                destination={afterCarousel}
            />
            <Typography {...styles.titleText}>{title}</Typography>
            <GridContainer
                {...styles.mainContainer}
                data-test-selector="banner"
            >
                {fields.showNavButtons && canUseCarousel ? (
                    <>
                        <GridItem {...styles.prevArrowGridItem} style={prevButtonStyle}>
                            {shuffledElements.length > slidesToScroll && (
                                <Button
                                    {...styles.prevArrowButton}
                                    onClick={() => embla && embla.scrollPrev()}
                                    disabled={!canScrollPrev}
                                    data-test-selector="prevBtn"
                                >
                                    <Icon src={ChevronLeft} {...styles.iconStyle} />
                                </Button>
                            )}
                        </GridItem>
                        <GridItem {...styles.carouselGridItem}>
                            <StyledWrapper {...styles.carouselWrapper}>
                                <StyledWrapper {...styles.carouselContainer} ref={emblaRef}>
                                    <StyledWrapper {...styles.carouselSlidesContainer} data-test-selector="slides">
                                        {shuffledElements.map((element, index) => getSlide(element, index))}
                                    </StyledWrapper>
                                </StyledWrapper>
                            </StyledWrapper>
                        </GridItem>
                        <GridItem {...styles.nextArrowGridItem} style={nextButtonStyle}>
                            {shuffledElements.length > slidesToScroll && (
                                <Button
                                    {...styles.nextArrowButton}
                                    onClick={() => embla && embla.scrollNext()}
                                    disabled={!canScrollNext}
                                    data-test-selector="nextBtn"
                                >
                                    <Icon src={ChevronRight} {...styles.iconStyle} />
                                </Button>
                            )}
                        </GridItem>
                    </>
                ) : (
                    <GridItem {...styles.carouselGridItem}>
                        <StyledWrapper {...styles.slidesContainer} data-test-selector="slides"
                            style={(fields.showNavButtons && !canUseCarousel ? {justifyContent: "center"} : {})}
                        >
                            {shuffledElements.map((element, index) => {
                                if (index >= slidesToScroll) {
                                    return;
                                }
                                return getSlide(element, index);
                            })}
                        </StyledWrapper>
                    </GridItem>
                )}
            </GridContainer>
            <span ref={afterCarousel} tabIndex={-1} />
        </>
    );
};

const widgetModule: WidgetModule = {
    component: withIsInShell(withTheme(CarouselBanner)),
    definition: {
        displayName: "Carousel Banner",
        group: "Common",
		icon: "gallery-thumbnails",
        fieldDefinitions: [
            {
                name: fields.fullScreenWidth,
                displayName: "Fullscreen width",
                tooltip: "The number of tiles will be adapted to the screen width and the carousel is NOT inside a row widget. If unchecked, this widget should be inside a Row widget.",
                editorTemplate: "CheckboxField",
                defaultValue: false,
                fieldType: "General",
            },
            {
                name: fields.maxNumberOfTiles,
                displayName: `Number Of Tiles at ${COMMON.width.site}px resolution`,
                tooltip: `Number of tiles at a resolution of ${COMMON.width.site}px. For larger resolutions (if 'Fullscreen width' is checked only), more tiles will be displayed proportionnally.`,
                editorTemplate: "IntegerField",
                min: 1,
                max: 6,
                defaultValue: 4,
                fieldType: "General",
            },
            {
                name: fields.showNavButtons,
                displayName: "Show navigation buttons",
                tooltip: "Show previous and next navigation buttons and allow to see all the sliders. Without these, only some slides will be displayed at random.",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
            },
            {
                name: fields.elements,
                editorTemplate: "ListField",
                getDisplay: ({ fields: { title } }) => title || "Item",
                defaultValue: [],
                fieldType: "Translatable",
                fieldDefinitions: [
                    {
                        name: "title",
                        tooltip: "Short title for the link, will not be displayed but will be used to describe the link for accessibility purpose.",
                        editorTemplate: "TextField",
                        defaultValue: "",
                        sortOrder: 0,
                    },
                    {
                        name: "image",
                        tooltip: "For better results, use all images of the same size.",
                        editorTemplate: "ImagePickerField",
                        defaultValue: "",
                    },
                    {
                        name: "link",
                        tooltip: "Url of the link",
                        displayName: "Link",
                        editorTemplate: "LinkField",
                        defaultValue: { type: "Page", value: "" },
                    },
                ],
            },
        ],
    },
};

export default widgetModule;
